import { apiClient } from './client';
import type {
  Anfrage,
  AnfrageListResponse,
  CreateAnfragePayload,
  CreateAnfrageResponse,
  PositionAnfrageInfo,
  PositionAnfragenMap,
  QuestionAnswersResponse,
  UpdateAnfrageItemPayload,
} from '@/types/anfragen';

export interface TrendDataPoint {
  date: string;
  count: number;
}

export interface TrendResponse {
  trend: TrendDataPoint[];
}

export interface TopReason {
  reason_id: number;
  title: string;
  count: number;
}

export interface TopReasonsResponse {
  reasons: TopReason[];
}

export interface GetAnfragenParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  status_id?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface LabelOption {
  shipping_cost_id: number;
  carrier: string;
  label: string;
  shipping_option_code: string | null;
}

export interface CreateLabelResponse {
  label_created: boolean;
  carrier: string;
  dhl_shipment_no?: string | null;
  dpd_parcel_no?: string | null;
  gls_return_order_id?: string | null;
  gls_parcel_id?: string | null;
  sendcloud_parcel_id?: number | null;
}

export const anfragenApi = {
  getAll: async (params?: GetAnfragenParams): Promise<AnfrageListResponse> => {
    const response = await apiClient.get<AnfrageListResponse>('/anfragen', {
      params,
    });
    return response.data;
  },

  getByUid: async (uid: string): Promise<Anfrage> => {
    const response = await apiClient.get<Anfrage>(`/anfragen/${uid}`);
    return response.data;
  },

  create: async (data: CreateAnfragePayload): Promise<CreateAnfrageResponse> => {
    const response = await apiClient.post<CreateAnfrageResponse>('/anfragen', data);
    return response.data;
  },

  updateStatus: async (
    uid: string,
    statusId: number,
    integrationId?: number,
  ): Promise<{ success: boolean; status_title: string; workflows_executed: number }> => {
    const response = await apiClient.put<{
      success: boolean;
      status_title: string;
      workflows_executed: number;
    }>(`/anfragen/${uid}/status`, {
      status_id: statusId,
      ...(integrationId ? { integration_id: integrationId } : {}),
    });
    return response.data;
  },

  reject: async (
    uid: string,
    rejectionReason: string,
  ): Promise<{ success: boolean; status_title: string; workflows_executed: number }> => {
    const response = await apiClient.post<{
      success: boolean;
      status_title: string;
      workflows_executed: number;
    }>(`/anfragen/${uid}/reject`, { rejection_reason: rejectionReason });
    return response.data;
  },

  delete: async (uid: string): Promise<void> => {
    await apiClient.delete(`/anfragen/${uid}`);
  },

  countsByStatus: async (): Promise<{ counts: Record<string, number>; total: number }> => {
    const response = await apiClient.get<{ counts: Record<string, number>; total: number }>(
      '/anfragen/counts-by-status',
    );
    return response.data;
  },

  getByDeliveryGroup: async (uid: string): Promise<PositionAnfragenMap> => {
    const response = await apiClient.get<Record<string, unknown>>(
      `/anfragen/by-delivery-group/${uid}`,
    );
    // Das Backend hängt an jede JSON-Objekt-Antwort customer_id an
    // (inject_customer_id in app.py). In dieser Map ist das keine Position.
    return Object.fromEntries(
      Object.entries(response.data ?? {}).filter(
        ([, value]) =>
          !!value &&
          typeof value === 'object' &&
          Array.isArray((value as PositionAnfrageInfo).anfragen),
      ),
    ) as PositionAnfragenMap;
  },

  updateItem: async (
    uid: string,
    itemId: number,
    payload: UpdateAnfrageItemPayload,
  ): Promise<{ success: boolean; id: number }> => {
    const response = await apiClient.put<{ success: boolean; id: number }>(
      `/anfragen/${uid}/items/${itemId}`,
      payload,
    );
    return response.data;
  },

  getQuestionAnswers: async (uid: string): Promise<QuestionAnswersResponse> => {
    const response = await apiClient.get<QuestionAnswersResponse>(
      `/anfragen/${uid}/question-answers`,
    );
    return response.data;
  },

  updateLanguage: async (
    uid: string,
    language: string,
  ): Promise<{ success: boolean; language: string }> => {
    const response = await apiClient.put<{ success: boolean; language: string }>(
      `/anfragen/${uid}/language`,
      { language },
    );
    return response.data;
  },

  trend: async (days = 30): Promise<TrendResponse> => {
    const response = await apiClient.get<TrendResponse>('/anfragen/trend', {
      params: { days },
    });
    return response.data;
  },

  topReasons: async (limit = 5): Promise<TopReasonsResponse> => {
    const response = await apiClient.get<TopReasonsResponse>('/anfragen/top-reasons', {
      params: { limit },
    });
    return response.data;
  },

  getLabelOptions: async (uid: string): Promise<LabelOption[]> => {
    const response = await apiClient.get<{ options: LabelOption[] }>(
      `/anfragen/${uid}/label-options`,
    );
    return response.data.options;
  },

  createLabel: async (uid: string, shippingCostId: number): Promise<CreateLabelResponse> => {
    const response = await apiClient.post<CreateLabelResponse>(`/anfragen/${uid}/label`, {
      shipping_cost_id: shippingCostId,
    });
    return response.data;
  },
};
